import React, { useEffect, useState } from 'react';
import apiClient, { getErrorMessage } from '../utils/api';
import './AccountTransfer.css';

interface Manager {
  userno: number;
  username: string;
}

interface TransferCandidates {
  count: number;
  source_userno: number;
}

const AccountTransfer: React.FC = () => {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [selectedSourceUserno, setSelectedSourceUserno] = useState<number | null>(null);
  const [selectedTargetUserno, setSelectedTargetUserno] = useState<number | null>(null);
  const [candidateCount, setCandidateCount] = useState<number | null>(null);
  const [targetCount, setTargetCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingTargetCount, setLoadingTargetCount] = useState(false);
  const [loadingXml, setLoadingXml] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchManagers();
  }, []);

  useEffect(() => {
    if (selectedSourceUserno) {
      fetchTransferCandidates();
    } else {
      setCandidateCount(null);
    }
  }, [selectedSourceUserno]);

  useEffect(() => {
    if (selectedTargetUserno) {
      fetchTargetManagerCount();
    } else {
      setTargetCount(null);
    }
  }, [selectedTargetUserno]);

  const fetchManagers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/account-transfer/managers');
      const data = response.data;
      console.log('Managers fetched successfully:', data.managers?.length || 0, 'managers');
      setManagers(data.managers || []);
    } catch (err: any) {
      console.error('Error fetching managers:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchTransferCandidates = async () => {
    if (!selectedSourceUserno) return;

    try {
      setLoadingCandidates(true);
      const response = await apiClient.post('/account-transfer/candidates', { 
        source_userno: selectedSourceUserno 
      });
      setCandidateCount(response.data.count);
    } catch (err: any) {
      console.error('Error fetching transfer candidates:', err);
      setCandidateCount(null);
      setError(getErrorMessage(err));
    } finally {
      setLoadingCandidates(false);
    }
  };

  const fetchTargetManagerCount = async () => {
    if (!selectedTargetUserno) return;

    try {
      setLoadingTargetCount(true);
      const response = await apiClient.post('/account-transfer/target-count', { 
        target_userno: selectedTargetUserno 
      });
      setTargetCount(response.data.count);
    } catch (err: any) {
      console.error('Error fetching target manager count:', err);
      setTargetCount(null);
      setError(getErrorMessage(err));
    } finally {
      setLoadingTargetCount(false);
    }
  };

  const getSelectedManagerName = (userno: number | null) => {
    if (!userno) return null;
    
    const manager = managers.find(m => m.userno === userno);
    if (manager?.username) {
      return manager.username;
    }
    
    // Fallback if manager not found (shouldn't happen in normal operation)
    return `Manager ID: ${userno}`;
  };

  const getSelectedManagerWithId = (userno: number | null) => {
    if (!userno) return null;
    
    const manager = managers.find(m => m.userno === userno);
    if (manager?.username) {
      return `${manager.username} ${userno}`;
    }
    
    return `Manager ID: ${userno}`;
  };

  const getAvailableTargetManagers = () => {
    return managers.filter(manager => manager.userno !== selectedSourceUserno);
  };

  const handleSourceChange = (userno: string) => {
    const usernoNum = parseInt(userno);
    setSelectedSourceUserno(usernoNum || null);
    setSelectedTargetUserno(null); // Reset target when source changes
    setTargetCount(null); // Reset target count when source changes
  };

  const handleTargetChange = (userno: string) => {
    const usernoNum = parseInt(userno);
    setSelectedTargetUserno(usernoNum || null);
  };

  const handleRefreshManagers = async () => {
    setError(null);
    setSelectedSourceUserno(null);
    setSelectedTargetUserno(null);
    setCandidateCount(null);
    setTargetCount(null);
    await fetchManagers();
  };


  const handleDownloadXml = async () => {
    if (!selectedSourceUserno || !selectedTargetUserno) {
      setError('Please select both source and target managers before downloading XML');
      return;
    }

    try {
      setLoadingXml(true);
      setError(null);

      // Get manager info from the dropdown selections - we already have this data!
      const sourceManager = managers.find(m => Number(m.userno) === Number(selectedSourceUserno));
      const targetManager = managers.find(m => Number(m.userno) === Number(selectedTargetUserno));

      // If we can't find the managers, it means there's a serious data issue
      if (!sourceManager || !targetManager) {
        throw new Error('Manager data corrupted. Please refresh the page and try again.');
      }

      const response = await apiClient.post('/account-transfer/download-xml', {
        source_userno: selectedSourceUserno,
        target_userno: selectedTargetUserno,
        source_username: sourceManager.username,
        target_username: targetManager.username,
        account_count: candidateCount
      }, {
        responseType: 'blob'
      });

      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'account_transfer.xml';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err: any) {
      console.error('Error downloading XML:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingXml(false);
    }
  };

  if (loading) {
    return (
      <div className="account-transfer-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading account managers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-transfer-container">
        <div className="error-message">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button onClick={handleRefreshManagers} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-transfer-container">
      <div className="transfer-header">
        <h1>🔄 Account Transfer</h1>
        <p className="subtitle">Transfer loan accounts between account managers</p>
        <button onClick={fetchManagers} className="refresh-button">
          🔄 Refresh Managers
        </button>
      </div>

      <div className="transfer-form">
        {/* Source Manager Card */}
        <div className="form-section">
          <h2>Select Source Manager</h2>
          <div className="input-group">
            <label htmlFor="source-manager">From Account Manager:</label>
            <select
              id="source-manager"
              value={selectedSourceUserno || ''}
              onChange={(e) => handleSourceChange(e.target.value)}
              className="manager-select"
            >
              <option value="">-- Select Source Manager --</option>
              {managers.map((manager) => (
                <option key={manager.userno} value={manager.userno}>
                  {manager.username}
                </option>
              ))}
            </select>
          </div>

          {selectedSourceUserno && (
            <div className="candidates-info">
              <div className="info-card">
                <h3>📊 Transfer Candidates</h3>
                {loadingCandidates ? (
                  <div className="loading-candidates">
                    <div className="mini-spinner"></div>
                    <span>Counting accounts...</span>
                  </div>
                ) : (
                  <div className="candidate-count">
                    <span className="count-number">{candidateCount?.toLocaleString() || 0}</span>
                    <span className="count-label">accounts available for transfer</span>
                  </div>
                )}
                <div className="source-info">
                  <p><strong>FROM:</strong> {getSelectedManagerWithId(selectedSourceUserno)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Target Manager Card */}
        <div className="form-section">
          <h2>Select Target Manager</h2>
          <div className="input-group">
            <label htmlFor="target-manager">To Account Manager:</label>
            <select
              id="target-manager"
              value={selectedTargetUserno || ''}
              onChange={(e) => handleTargetChange(e.target.value)}
              className="manager-select"
              disabled={!selectedSourceUserno}
            >
              <option value="">
                {selectedSourceUserno ? '-- Select Target Manager --' : '-- First select source manager --'}
              </option>
              {getAvailableTargetManagers().map((manager) => (
                <option key={manager.userno} value={manager.userno}>
                  {manager.username}
                </option>
              ))}
            </select>
          </div>

          {selectedTargetUserno && (
            <div className="target-info">
              <div className="info-card">
                <h3>📈 Current Account Load</h3>
                {loadingTargetCount ? (
                  <div className="loading-candidates">
                    <div className="mini-spinner"></div>
                    <span>Counting current accounts...</span>
                  </div>
                ) : (
                  <div className="candidate-count">
                    <span className="count-number">{targetCount?.toLocaleString() || 0}</span>
                    <span className="count-label">current accounts</span>
                  </div>
                )}
                <div className="source-info">
                  <p><strong>TO:</strong> {getSelectedManagerWithId(selectedTargetUserno)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Transfer Summary Card */}
        {selectedSourceUserno && selectedTargetUserno && managers.length > 0 && (
          <div className="form-section">
            <h2>Transfer Summary</h2>
            <div className="transfer-summary">
              <div className="summary-card">
                <h3>🎯 Transfer Details</h3>
                <div className="summary-content">
                  <div className="summary-row highlight">
                    <span className="label">🔄 FROM:</span>
                    <span className="value source-name">{getSelectedManagerWithId(selectedSourceUserno)}</span>
                  </div>
                  <div className="summary-row highlight">
                    <span className="label">➡️ TO:</span>
                    <span className="value target-name">{getSelectedManagerWithId(selectedTargetUserno)}</span>
                  </div>
                  <div className="summary-row count-row">
                    <span className="label">📊 Accounts to Transfer:</span>
                    <span className="value count">{candidateCount?.toLocaleString() || 0}</span>
                  </div>
                  <div className="summary-row count-row">
                    <span className="label">📈 Target Manager Current Accounts:</span>
                    <span className="value count">
                      {loadingTargetCount ? (
                        <span className="loading-text">Loading...</span>
                      ) : (
                        targetCount?.toLocaleString() || 'N/A'
                      )}
                    </span>
                  </div>
                </div>
                <div className="transfer-actions">
                  <button 
                    className="transfer-button centered"
                    onClick={handleDownloadXml}
                    disabled={!selectedSourceUserno || !selectedTargetUserno || candidateCount === 0 || loadingXml || managers.length === 0}
                  >
                    {loadingXml ? '⏳ Generating...' : '📄 Download XML'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {selectedSourceUserno && selectedTargetUserno && candidateCount === 0 && (
        <div className="warning-message">
          <h3>⚠️ No Accounts Available</h3>
          <p>
            There are no accounts available for transfer from {getSelectedManagerName(selectedSourceUserno)}. 
            This could mean all accounts are in a non-transferable status or the manager has no active accounts.
          </p>
        </div>
      )}
    </div>
  );
};

export default AccountTransfer;
